import Box from '@mui/material/Box'
import {
  AssignDiscipline,
  AssignDisciplineNames,
  AssignProject,
  AssignStatus,
} from '../../models/Review'
import { Divider, Stack, Typography } from '@mui/material'
import NestedContentComponent from '../NestedContentComponent'
import {
  ElectricalEngineeringIcon,
  StructuralEngineeringIcon,
  MechanicalEngineeringIcon,
  PlumbingEngineeringIcon,
} from '../Svgs'
import { getCategoryColor, getStatusColor } from '../../utils/ComponentsUtil'
import ManageEngineerComponent from './ManageEngineerComponent'
import AssignEngineerComponent from './AssignEngineerComponent'
import AssignEngineerRequestedComponent from './AssignEngineerRequestedComponent'
import { UserRole, UserRoleNames } from '../../models/AccessControl'
import AssignProjectRequestedComponent from './AssignProjectRequestedComponent'
import ManageProjectComponent from './ManageProjectComponent'

interface AssignAndManageProjectProps {
  role: UserRole
  project: AssignProject
}

const AssignAndManageProjectComponent: React.FC<
  AssignAndManageProjectProps
> = ({ role, project }) => {
  const getCategoryIcon = (category: AssignDisciplineNames) => {
    switch (category) {
      case AssignDisciplineNames.Mechanical:
        return (
          <MechanicalEngineeringIcon
            color={getCategoryColor(AssignDisciplineNames.Mechanical)}
          />
        )
      case AssignDisciplineNames.Electrical:
        return (
          <ElectricalEngineeringIcon
            color={getCategoryColor(AssignDisciplineNames.Electrical)}
          />
        )
      case AssignDisciplineNames.Structural:
        return (
          <StructuralEngineeringIcon
            color={getCategoryColor(AssignDisciplineNames.Structural)}
          />
        )
      case AssignDisciplineNames.Plumbing:
        return (
          <PlumbingEngineeringIcon
            color={getCategoryColor(AssignDisciplineNames.Plumbing)}
          />
        )
    }
  }

  const getProjectStatus = (status: AssignStatus, completedTasksNb: number) => {
    switch (role.name) {
      case UserRoleNames.EngineeringAdmin:
        switch (status) {
          case AssignStatus.NotAssigned:
            return 'Not Assigned'
          case AssignStatus.AssignRequested:
            return 'Assign Requested'
          case AssignStatus.Reject:
            return 'Rejected'
          case AssignStatus.InProgress:
            if (!!completedTasksNb) {
              return 'Assigned, In Progress'
            } else {
              return 'Assigned, Not Started'
            }
          default:
            return status
        }
      case UserRoleNames.LeadingElectricalEngineer:
      case UserRoleNames.LeadingStructuralEngineer:
      case UserRoleNames.ElectricalEngineer:
      case UserRoleNames.StructuralEngineer:
        switch (status) {
          case AssignStatus.AssignRequested:
            return 'Assigned, Not Started'
          case AssignStatus.InProgress:
            return 'In Progress'
          default:
            return status
        }
    }
  }

  const adminAssignContent = (assignedDiscipline: AssignDiscipline) => {
    switch (assignedDiscipline.discipline_status) {
      case AssignStatus.NotAssigned:
      case AssignStatus.Reject:
        return <AssignEngineerComponent discipline={assignedDiscipline} />
      case AssignStatus.AssignRequested:
        return (
          <AssignEngineerRequestedComponent discipline={assignedDiscipline} />
        )
      case AssignStatus.InProgress:
        return <ManageEngineerComponent discipline={assignedDiscipline} />
    }
  }

  const engineerAssignContent = (assignedDiscipline: AssignDiscipline) => {
    switch (assignedDiscipline.discipline_status) {
      case AssignStatus.AssignRequested:
        return (
          <AssignProjectRequestedComponent
            discipline={assignedDiscipline}
            fileId={project.file_id}
          />
        )
      case AssignStatus.InProgress:
        return <ManageProjectComponent discipline={assignedDiscipline} />
    }
  }

  const renderAssignContent = (assignedDiscipline: AssignDiscipline) => {
    switch (role.name) {
      case UserRoleNames.EngineeringAdmin:
        return adminAssignContent(assignedDiscipline)
      case UserRoleNames.LeadingElectricalEngineer:
      case UserRoleNames.LeadingStructuralEngineer:
      case UserRoleNames.ElectricalEngineer:
      case UserRoleNames.StructuralEngineer:
        return engineerAssignContent(assignedDiscipline)
    }
  }

  return (
    <Stack gap='0.4rem'>
      {project.disciplines.map((assignedDiscipline, index) => (
        <Box
          key={index}
          sx={{
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            borderRadius: '6px',
            border: '1px solid #EBEBEB',
            padding: '1rem',
            boxSizing: 'border-box',
            boxShadow: '0px 3px 5px 0px #2F2F2F1A',
          }}
        >
          <NestedContentComponent
            title={
              <Box
                display={'flex'}
                width={'100%'}
                justifyContent={'space-between'}
                mr={'1.5rem'}
              >
                <Box
                  sx={{
                    display: 'flex',
                    gap: '0.75rem',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {getCategoryIcon(assignedDiscipline.discipline)}
                  <Typography
                    sx={{
                      color: '#04002E',
                      fontSize: '0.76rem',
                      lineHeight: 'normal',
                      fontWeight: 500,
                      textTransform: 'capitalize',
                    }}
                  >
                    {assignedDiscipline.discipline
                      .toString()
                      .toLocaleLowerCase()}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    color: getStatusColor(
                      role,
                      assignedDiscipline.discipline_status,
                      0,
                    ),
                    fontSize: '0.61rem',
                    lineHeight: '1.5rem',
                  }}
                >
                  {getProjectStatus(
                    assignedDiscipline.discipline_status,
                    0,
                  )?.toUpperCase()}
                </Typography>
              </Box>
            }
            arrowColor='#04002E'
          >
            <Divider flexItem sx={{ mb: '0.75rem ' }} />
            {renderAssignContent(assignedDiscipline)}
          </NestedContentComponent>
        </Box>
      ))}
    </Stack>
  )
}

export default AssignAndManageProjectComponent
